/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1789689600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Sep 18 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-retail",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Google Retail",
          "depth": 0,
          "id": "connect-to-google-retail"
        }, {
          "value": "Migrate your Merchant Center account",
          "depth": 1,
          "id": "migrate-your-merchant-center-account"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Products",
          "depth": 1,
          "id": "products"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      code: "code",
      ul: "ul",
      li: "li",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CommonErrorsPreface, PylonLink, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Google Retail lists your products across Google's shopping surfaces, including Shopping ads, free listings, and vehicle ads. Hightouch writes product listings from your model into a Google Merchant Center account through the ", _jsx(_components.a, {
          href: "https://developers.google.com/merchant/api",
          children: "Merchant API"
        }), ", keeping your catalog in sync with your warehouse."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#products",
                children: _jsx(_components.strong, {
                  children: "Products"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records from your model as product listings"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/merchant/api/reference/rest/products_v1/accounts.productInputs/insert",
                children: "Insert a product ↗"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google-retail",
        children: "Connect to Google Retail"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You need access to a ", _jsx(_components.a, {
            href: "https://www.google.com/retail/",
            children: "Google Merchant Center\naccount"
          }), " to sync listings."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Retail"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Google Retail"
        }), " and log into the Google account that has access to your Merchant Center account. Once you're redirected back to Hightouch, enter your ", _jsx(_components.strong, {
          children: "Merchant ID"
        }), " to complete setup."]
      }), "\n", _jsx(_components.h3, {
        id: "migrate-your-merchant-center-account",
        children: "Migrate your Merchant Center account"
      }), "\n", _jsxs(_components.p, {
        children: ["The Merchant API reads and writes through data sources, and that service is only available to Merchant Center accounts migrated to ", _jsx(_components.strong, {
          children: "single locale feeds and data target split"
        }), ". If your account hasn't been migrated, complete the migration in Merchant Center before syncing. See Google's ", _jsx(_components.a, {
          href: "https://developers.google.com/merchant/api/guides/data-sources/overview",
          children: "data sources overview"
        }), " for the migration steps."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Retail destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Retail destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "products",
        children: "Products"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sync writes product listings into one Merchant Center account. Select the ", _jsx(_components.strong, {
          children: "Product category"
        }), " that your products are listed under. If your model spans several categories, map the category from a model column instead of setting one value for the whole sync."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the model column that contains your ", _jsx(_components.strong, {
          children: "Offer ID"
        }), ", the identifier that uniquely identifies each product within your Merchant Center account."]
      }), "\n", _jsxs(_components.p, {
        children: ["Google identifies a product by the combination of its offer ID, content language, feed label, and channel, not by offer ID alone. Map ", _jsx(_components.strong, {
          children: "Content language"
        }), " and ", _jsx(_components.strong, {
          children: "Feed label"
        }), " on every sync. If you map ", _jsx(_components.strong, {
          children: "Target country"
        }), " instead of ", _jsx(_components.strong, {
          children: "Feed label"
        }), ", Hightouch uses the target country as the feed label. Rows missing these values fail, and deletes can't locate the product to remove."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["Map model columns to Google's product attributes. Attributes you leave unmapped aren't sent. See Google's ", _jsx(_components.a, {
          href: "https://support.google.com/merchants/answer/7052112",
          children: "product data specification"
        }), " for what each attribute means and which ones your products require."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Custom attributes"
        }), " for merchant-provided attributes that the API doesn't expose as named fields. Hightouch sends every custom attribute value as a string."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch converts your mapped values into the formats the Merchant API expects, so you can map values as they're stored in your warehouse:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Mapped value"
            }), _jsx(_components.th, {
              children: "Sent to Google as"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Price value and currency"
            }), _jsx(_components.td, {
              children: "An amount in micros plus a currency code"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["A date such as ", _jsx(_components.code, {
                children: "2026-09-18"
              })]
            }), _jsx(_components.td, {
              children: "A timestamp at midnight UTC"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["A start and end date separated by a ", _jsx(_components.code, {
                children: "/"
              })]
            }), _jsx(_components.td, {
              children: "An interval with a start and end time"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Enum spellings such as ", _jsx(_components.code, {
                children: "in stock"
              }), ", ", _jsx(_components.code, {
                children: "A+++"
              }), ", or ", _jsx(_components.code, {
                children: "2 day"
              })]
            }), _jsxs(_components.td, {
              children: ["The canonical value the API accepts, such as ", _jsx(_components.code, {
                children: "IN_STOCK"
              }), ", ", _jsx(_components.code, {
                children: "APPP"
              }), ", or ", _jsx(_components.code, {
                children: "TWO_DAY"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "A price that's mapped but unusable rejects the row rather than reaching Google. This covers an empty value, a value that isn't a number, and a value with no currency, each of which would otherwise list the product at the wrong price."
      }), "\n", _jsx(_components.p, {
        children: "Values Hightouch doesn't recognize pass through unchanged, so Google's own error names the attribute."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "kind"
          }), ", ", _jsx(_components.code, {
            children: "source"
          }), ", ", _jsx(_components.code, {
            children: "taxes"
          }), ", and ", _jsx(_components.code, {
            children: "taxCategory"
          }), " no longer exist in the Merchant\nAPI and are dropped before the request. Configure tax settings at the account\nlevel in Merchant Center instead."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "vehicle-attributes",
        children: "Vehicle attributes"
      }), "\n", _jsxs(_components.p, {
        children: ["Products in the ", _jsx(_components.strong, {
          children: "Cars, Trucks & Vans (916)"
        }), " category have an additional ", _jsx(_components.strong, {
          children: "Vehicle specific options"
        }), " section for vehicle-only attributes such as fulfillment, mileage, and trim. This section appears only when the sync's product category is 916. Read more about ", _jsx(_components.a, {
          href: "https://support.google.com/merchants/answer/11190480",
          children: "vehicle ads attributes"
        }), " in Google's docs."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["When a row leaves your model's query results, Hightouch can ", _jsx(_components.strong, {
          children: "Delete the product in Google Retail"
        }), ", which removes the product listing from Merchant Center."]
      }), "\n", _jsx(_components.p, {
        children: "Deletes are addressed by offer ID, content language, feed label, and channel. Keep those columns mapped and stable, because a row whose identifying values have changed points at a different product."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Each sync replaces the whole product."
        }), " Hightouch writes products with Google's insert operation, which replaces the existing product listing rather than patching it. Any attribute you don't map is cleared in Merchant Center on the next sync. To preserve an attribute Hightouch doesn't manage, map it."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Hightouch chooses your data source automatically."
        }), " The Merchant API requires every product write to name a data source. Hightouch resolves one for your account at the start of each run and never stores it on the sync:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["If the account has the ", _jsx(_components.strong, {
            children: "Content API"
          }), " data source that Google created for it, Hightouch reuses it. Products written before the Merchant API migration live there, so reusing it keeps them in place."]
        }), "\n", _jsx(_components.li, {
          children: "If the account has exactly one other writable data source, Hightouch uses that one."
        }), "\n", _jsxs(_components.li, {
          children: ["If the account has no writable data source, Hightouch creates one named ", _jsx(_components.strong, {
            children: "Hightouch"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "If the account has more than one writable data source, the sync fails rather than guessing. Choosing the wrong one would move products out of the source that currently owns them."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Only data sources with an input type of API are eligible. File, UI, and autofeed sources are managed outside the API and are never written to."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Local-channel products need their own data source."
        }), " Google keeps separate data sources for online and local products. If your model has rows with a ", _jsx(_components.strong, {
          children: "Channel"
        }), " of ", _jsx(_components.code, {
          children: "local"
        }), " and the account has no single local-channel data source, those rows fail. Online-channel rows are unaffected."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Google Retail, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nOther destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After a sync run, open ", _jsx(_components.strong, {
          children: "Products > All products"
        }), " in ", _jsx(_components.a, {
          href: "https://merchants.google.com/",
          children: "Merchant Center"
        }), " and search for one of your offer IDs. Newly written products appear with a ", _jsx(_components.strong, {
          children: "Pending"
        }), " status while Google processes them, then move to ", _jsx(_components.strong, {
          children: "Active"
        }), " or surface an item-level issue. Google's processing delay means a product can take several hours to become active even though the Hightouch sync succeeded."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "multiple-writable-data-sources",
        children: "Multiple writable data sources"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-kotlin",
          children: ["Google Retail: ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " Merchant Center account has multiple writable ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "data"
          }), " sources (", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"...\""
          }), ") and Hightouch cannot safely choose one. Contact Hightouch support to ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " the target ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "data"
          }), " source ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " sync.\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The Merchant Center account has more than one API data source that Hightouch could write products into. Writing through the wrong one moves products out of the data source that currently owns them, so Hightouch stops instead of choosing."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Please ", _jsx(PylonLink, {
          pylonText: "Hello, I need help setting the Google Retail data source for my sync.",
          linkText: "contact Hightouch support"
        }), " with the name of the data source the sync should write to. Support can set it on the sync configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "failed-to-access-merchant-center-data-sources",
        children: "Failed to access Merchant Center data sources"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Failed ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " access Merchant Center data sources: ... The Merchant API data sources service requires the Merchant Center account ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " be migrated ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"single locale feeds and data target split\""
          }), ".\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The Merchant Center account hasn't been migrated to single locale feeds and data target split, so the Merchant API can't list its data sources."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Complete the migration in Merchant Center, then re-run the sync. See Google's ", _jsx(_components.a, {
          href: "https://developers.google.com/merchant/api/guides/data-sources/overview",
          children: "data sources overview"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "no-local-channel-data-source",
        children: "No local-channel data source"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["Google Retail (Merchant API) ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "sync"
          }), " has no local-channel data ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "source"
          }), " but this ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "sync"
          }), " writes local-channel products.\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sync includes rows with a ", _jsx(_components.strong, {
          children: "Channel"
        }), " of ", _jsx(_components.code, {
          children: "local"
        }), ", but the Merchant Center account has either no local-channel API data source or more than one."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Create a single local-channel data source in Merchant Center, or remove local-channel rows from the model. If the account already has several, ", _jsx(PylonLink, {
          pylonText: "Hello, I need help setting the Google Retail local data source for my sync.",
          linkText: "contact Hightouch support"
        }), " to set the one this sync should use."]
      }), "\n", _jsxs(_components.p, {
        children: ["For error codes returned by Google, see the Merchant API's ", _jsx(_components.a, {
          href: "https://developers.google.com/merchant/api/guides/handle-errors",
          children: "errors reference"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
